import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Sparkles, RefreshCw, AlertCircle, MapPin,
  Tag, X, TrendingUp, Users, ChevronRight, Hash, UserCheck
} from 'lucide-react';
import FoodCardIcon from '../components/FoodCardIcon';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import CreatePostModal from '../components/CreatePostModal';
import CreateFoodCardModal from '../components/CreateFoodCardModal';
import CommentsThread from '../components/CommentsThread';
import EditPostModal from '../components/EditPostModal';
import FoodCard from '../components/FoodCard';
import SuggestedUsers from '../components/SuggestedUsers';
import TrendingReposts from '../components/TrendingReposts';
import WhatsCookingSidebar from '../components/WhatsCookingSidebar';
import { StoriesRow, CreateStoryModal, StoryViewer } from '../components/Stories';
import { useFoodlist } from '../contexts/FoodlistContext';
import { useWebSocket, WSEventType } from '../contexts/WebSocketContext';
import RealTimeFeedIndicator from '../components/RealTimeFeedIndicator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostSkeleton = () => (
  <Card className="overflow-hidden border border-gray-200 animate-pulse">
    <CardContent className="p-4 pb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-200 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-gray-200 rounded-xl w-1/3" />
          <div className="h-3 bg-gray-200 rounded-xl w-1/4" />
        </div>
      </div>
    </CardContent>
    <div className="w-full aspect-square bg-gray-200" />
    <CardContent className="p-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded-xl w-3/4" />
      <div className="h-3 bg-gray-200 rounded-xl w-1/2" />
    </CardContent>
  </Card>
);

const GridSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} className="overflow-hidden animate-pulse">
        <div className="h-52 bg-gray-200" />
        <CardContent className="p-4 space-y-2">
          <div className="h-4 bg-gray-200 rounded-xl w-3/4" />
          <div className="h-3 bg-gray-200 rounded-xl w-1/2" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const PeopleCard = ({ suggestion, onFollow, isFollowing }) => (
  <div className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
    <Link to={`/profile/${suggestion.user_id}`}>
      <Avatar className="w-11 h-11 flex-shrink-0">
        <AvatarImage src={suggestion.picture} />
        <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-sm font-bold">
          {suggestion.name?.[0]?.toUpperCase() || 'F'}
        </AvatarFallback>
      </Avatar>
    </Link>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 truncate">{suggestion.name}</p>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {suggestion.shared_cuisines?.slice(0, 2).map(c => (
          <span key={c} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{c}</span>
        ))}
        {suggestion.shared_count > 0 && (
          <span className="text-[10px] text-gray-400">{suggestion.shared_count} shared tastes</span>
        )}
      </div>
    </div>
    <button onClick={() => onFollow(suggestion.user_id, isFollowing)}
      className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-2xl transition-all ${
        isFollowing ? 'bg-[#FFF5F0] text-[#FF6B35] border border-[#FF6B35]/30' : 'bg-[#FF6B35] text-white hover:bg-[#E85D04]'
      }`}>
      {isFollowing ? 'Following' : 'Follow'}
    </button>
  </div>
);

const SECTIONS = [
  { key: 'feed',      label: 'Feed',      icon: MapPin },
  { key: 'following', label: 'Following', icon: UserCheck },
  { key: 'forYou',    label: 'For You',   icon: Sparkles },
  { key: 'trending',  label: 'Trending',  icon: TrendingUp },
  { key: 'people',    label: 'People',    icon: Users },
];

// ── Main Foodie Feed Page ─────────────────────────────────────────────────────
const Foodie = () => {
  const { toast }            = useToast();
  const { locationString }   = useLocation();
  const { user }             = useAuth();
  const { toggleFoodlist, isInFoodlist } = useFoodlist();
  const loaderRef            = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') || '';

  const [activeSection, setActiveSection] = useState('feed');

  // Feed state
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]           = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore]       = useState(true);

  // Following feed state
  const [followingPosts, setFollowingPosts] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [followingError, setFollowingError] = useState(null);

  // Discover state
  const [forYou, setForYou]         = useState([]);
  const [trending, setTrending]     = useState([]);
  const [people, setPeople]         = useState([]);
  const [topCuisines, setTopCuisines] = useState([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);

  // Shared state
  const [followingIds, setFollowingIds] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showFoodCardModal, setShowFoodCardModal] = useState(false);
  const [editPost, setEditPost]     = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, post: null });
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [deletingPost, setDeletingPost] = useState(null);

  // Delete post handler
  const handleDeletePost = async (postId) => {
    setDeletingPost(postId);
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete post');
      
      // Remove from all feed states
      setPosts(prev => prev.filter(p => p.post_id !== postId));
      setForYouPosts(prev => prev.filter(p => p.post_id !== postId));
      setTrendingPosts(prev => prev.filter(p => p.post_id !== postId));
      setFollowingPosts(prev => prev.filter(p => p.post_id !== postId));
      
      toast({ title: 'Post deleted successfully' });
    } catch (err) {
      toast({ title: 'Failed to delete post', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingPost(null);
    }
  };

  // Real-time WebSocket
  const { addListener, isConnected } = useWebSocket();

  // Trending tags
  const [trendingTags, setTrendingTags] = useState([]);

  // Stories
  const [storyGroups, setStoryGroups] = useState([]);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [viewingStory, setViewingStory] = useState(null);

  const city = locationString?.split(',')[0] || '';

  const clearTag = () => { setSearchParams({}); };

  // ── Fetch stories ──
  const fetchStories = useCallback(() => {
    fetch(`${API_URL}/api/stories/feed`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { story_groups: [] })
      .then(d => setStoryGroups(d.story_groups || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  // ── Fetch trending tags ──
  useEffect(() => {
    const params = city ? `?city=${encodeURIComponent(city)}` : '';
    fetch(`${API_URL}/api/posts/trending-tags${params}`)
      .then(r => r.ok ? r.json() : { tags: [] })
      .then(d => setTrendingTags(d.tags || []))
      .catch(() => {});
  }, [city]);

  // ── Feed fetch ──
  const fetchPosts = useCallback(async (cursor = null, append = false) => {
    const setter = append ? setLoadingMore : setLoading;
    setter(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: '12' });
      if (city) params.set('city', city);
      if (activeTag) params.set('tag', activeTag);
      if (cursor) params.set('cursor', cursor);
      const res = await fetch(`${API_URL}/api/posts/feed?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load feed');
      const data = await res.json();
      setPosts(prev => append ? [...prev, ...(data.posts || [])] : (data.posts || []));
      setNextCursor(data.next_cursor || null);
      setHasMore(data.has_more || false);
    } catch (err) { setError(err.message); }
    finally { setter(false); }
  }, [city, activeTag]);

  // ── Following Feed fetch ──
  const fetchFollowingPosts = useCallback(async () => {
    if (!user) {
      setFollowingPosts([]);
      setFollowingError('Sign in to see posts from people you follow');
      return;
    }
    setFollowingLoading(true);
    setFollowingError(null);
    try {
      const params = new URLSearchParams({ limit: '20', tab: 'following' });
      if (city) params.set('city', city);
      const res = await fetch(`${API_URL}/api/posts/feed?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load following feed');
      const data = await res.json();
      setFollowingPosts(data.posts || []);
      if (data.message) setFollowingError(data.message);
    } catch (err) { setFollowingError(err.message); }
    finally { setFollowingLoading(false); }
  }, [city, user]);

  // ── Discover fetch ──
  const fetchDiscover = useCallback(async () => {
    setDiscoverLoading(true);
    const baseParams = city ? `city=${encodeURIComponent(city)}` : '';
    const tagParam = activeTag ? `tag=${encodeURIComponent(activeTag)}` : '';
    const qs = [baseParams, tagParam].filter(Boolean).join('&');
    const qsMark = qs ? `?${qs}` : '';

    const [recoRes, feedRes, discoverRes] = await Promise.allSettled([
      fetch(`${API_URL}/api/posts/recommendations${qsMark}`, { credentials: 'include' }),
      fetch(`${API_URL}/api/posts/feed${qsMark ? qsMark + '&' : '?'}limit=30`, { credentials: 'include' }),
      fetch(`${API_URL}/api/social/discover`, { credentials: 'include' }),
    ]);

    if (recoRes.status === 'fulfilled' && recoRes.value.ok) {
      const d = await recoRes.value.json();
      setForYou(d.posts || []);
    }
    if (feedRes.status === 'fulfilled' && feedRes.value.ok) {
      const d = await feedRes.value.json();
      const allPosts = d.posts || [];
      const sorted = [...allPosts].sort((a, b) => ((b.likes_count || 0) + (b.saves_count || 0)) - ((a.likes_count || 0) + (a.saves_count || 0)));
      setTrending(sorted.slice(0, 6));
      const cuisineMap = {};
      allPosts.forEach(p => { if (p.cuisine) cuisineMap[p.cuisine] = (cuisineMap[p.cuisine] || 0) + 1; });
      setTopCuisines(Object.entries(cuisineMap).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, count]) => ({ name, count })));
    }
    if (discoverRes.status === 'fulfilled' && discoverRes.value.ok) {
      const d = await discoverRes.value.json();
      setPeople(d.suggestions || []);
    }
    setDiscoverLoading(false);
  }, [city, activeTag]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // Fetch discover data when switching to those tabs
  useEffect(() => {
    if (activeSection !== 'feed' && activeSection !== 'following' && forYou.length === 0 && trending.length === 0) {
      fetchDiscover();
    }
    // Fetch following feed when that tab is active
    if (activeSection === 'following' && followingPosts.length === 0) {
      fetchFollowingPosts();
    }
  }, [activeSection]); // eslint-disable-line

  // Load following IDs
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/social/following-ids`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : {})
      .then(d => { if (d.following_ids) setFollowingIds(new Set(d.following_ids)); })
      .catch(() => {});
  }, [user]);

  // Infinite scroll (only for feed tab)
  useEffect(() => {
    if (activeSection !== 'feed') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && nextCursor) {
        fetchPosts(nextCursor, true);
      }
    }, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, nextCursor, fetchPosts, activeSection]);

  // ── Real-time WebSocket Event Listeners ──
  useEffect(() => {
    // Handle new posts
    const cleanupNewPost = addListener(WSEventType.NEW_POST, (postData) => {
      // Add new post to the top of the feed
      setPosts(prev => [postData, ...prev]);
      setForYou(prev => [postData, ...prev]);
    });

    // Handle post likes
    const cleanupLike = addListener(WSEventType.POST_LIKED, (data) => {
      const updateLikes = arr => arr.map(p => 
        p.post_id === data.post_id 
          ? { ...p, likes_count: data.likes_count }
          : p
      );
      setPosts(updateLikes);
      setForYou(updateLikes);
      setTrending(updateLikes);
      setFollowingPosts(updateLikes);
    });

    // Handle post unlikes
    const cleanupUnlike = addListener(WSEventType.POST_UNLIKED, (data) => {
      const updateLikes = arr => arr.map(p => 
        p.post_id === data.post_id 
          ? { ...p, likes_count: data.likes_count }
          : p
      );
      setPosts(updateLikes);
      setForYou(updateLikes);
      setTrending(updateLikes);
      setFollowingPosts(updateLikes);
    });

    // Handle new comments
    const cleanupComment = addListener(WSEventType.POST_COMMENTED, (data) => {
      const updateComments = arr => arr.map(p => 
        p.post_id === data.post_id 
          ? { ...p, comments_count: data.comments_count }
          : p
      );
      setPosts(updateComments);
      setForYou(updateComments);
      setTrending(updateComments);
      setFollowingPosts(updateComments);
    });

    return () => {
      cleanupNewPost();
      cleanupLike();
      cleanupUnlike();
      cleanupComment();
    };
  }, [addListener]);

  // ── Handlers ──
  const handleLike = async (postId, currentlyLiked) => {
    if (!user) { toast({ title: 'Sign in to like', variant: 'destructive' }); return; }
    const update = arr => arr.map(p => p.post_id === postId
      ? { ...p, viewer_liked: !currentlyLiked, likes_count: (p.likes_count || 0) + (currentlyLiked ? -1 : 1) }
      : p);
    setPosts(update); setForYou(update); setTrending(update); setFollowingPosts(update);
    try { await fetch(`${API_URL}/api/posts/${postId}/like`, { method: 'POST', credentials: 'include' }); } catch {}
  };

  const handleSave = async (postId, currentlySaved) => {
    if (!user) { toast({ title: 'Sign in to save', variant: 'destructive' }); return; }
    const update = arr => arr.map(p => p.post_id === postId
      ? { ...p, viewer_saved: !currentlySaved, saves_count: (p.saves_count || 0) + (currentlySaved ? -1 : 1) }
      : p);
    setPosts(update); setForYou(update); setTrending(update); setFollowingPosts(update);
    try { await fetch(`${API_URL}/api/posts/${postId}/save`, { method: 'POST', credentials: 'include' }); } catch {}
  };

  const handleShare = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/share`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (navigator.share) {
        await navigator.share({ title: 'FoodQuest', url: data.share_url });
      } else {
        await navigator.clipboard.writeText(data.share_url).catch(() => {});
        toast({ title: 'Link Copied!' });
      }
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, shares: (p.shares || 0) + 1 } : p));
    } catch {}
  };

  const handleFollow = async (targetId, isFollowingArg) => {
    if (!user) { toast({ title: 'Sign in to follow', variant: 'destructive' }); return; }
    const isFollowing = typeof isFollowingArg === 'boolean' ? isFollowingArg : followingIds.has(targetId);
    try {
      if (isFollowing) {
        await fetch(`${API_URL}/api/social/unfollow/${targetId}`, { method: 'DELETE', credentials: 'include' });
        setFollowingIds(prev => { const s = new Set(prev); s.delete(targetId); return s; });
        toast({ title: 'Unfollowed' });
      } else {
        await fetch(`${API_URL}/api/social/follow/${targetId}`, { method: 'POST', credentials: 'include' });
        setFollowingIds(prev => new Set([...prev, targetId]));
        toast({ title: 'Following!' });
      }
    } catch {}
  };

  const handleSaveToFoodlist = async (post) => {
    if (!user) { toast({ title: 'Sign in to save', variant: 'destructive' }); return; }
    const dish = {
      name: post.venue_name || post.caption?.slice(0, 40) || 'Dish',
      restaurant: post.venue_name || 'Restaurant',
      location: post.location || city,
      image: post.image_url || post.thumb_url || '',
      cuisine: post.cuisine || '',
    };
    await toggleFoodlist(dish);
    toast({ title: 'Saved to Foodlist!', description: `${dish.name} added to your collection.` });
  };

  const handlePostCreated = (newPost) => {
    setPosts(prev => [{ ...newPost, likes_count: 0, saves_count: 0, viewer_liked: false, viewer_saved: false }, ...prev]);
    toast({ title: 'Post shared!', description: 'Your food moment is live in the feed.' });
  };

  const handlePostEdited = (updatedPost) => {
    setPosts(prev => prev.map(p => p.post_id === updatedPost.post_id ? { ...p, ...updatedPost } : p));
    toast({ title: 'Post updated!' });
  };

  const handleRefresh = () => {
    if (activeSection === 'feed') fetchPosts();
    else if (activeSection === 'following') fetchFollowingPosts();
    else fetchDiscover();
  };

  const isLoading = activeSection === 'feed' ? loading : (activeSection === 'following' ? followingLoading : discoverLoading);
  const displayPosts = activeSection === 'trending' ? trending : forYou;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Real-time Feed Indicator */}
      <RealTimeFeedIndicator 
        onNewPost={(post) => {
          setPosts(prev => [post, ...prev]);
          setForYou(prev => [post, ...prev]);
        }}
      />
      
      {/* Hero – FoodQuest sub-style (matching Promote page) */}
      <div className="relative" style={{ minHeight: '240px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1600&q=80)' }} />
        <div className="relative h-full flex flex-col justify-end max-w-5xl mx-auto px-4 sm:px-6" style={{ minHeight: '240px' }}>
          <div className="w-full md:w-fit bg-black/45 backdrop-blur-md rounded-2xl p-4 md:p-5 mb-4">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-[3px] rounded-full bg-[#FF6B35] self-stretch min-h-[50px] flex-shrink-0" />
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.1] tracking-tight">Foodie Feed</h1>
                {locationString ? (
                  <p className="text-white/80 text-xs md:text-sm mt-1.5 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5 text-[#F4C430]" />
                    See what others are eating. Build your food journey
                  </p>
                ) : (
                  <p className="text-white/70 text-xs md:text-sm mt-1.5">See what others are eating. Build your food journey</p>
                )}
              </div>
            </div>
          </div>
          <div className="mb-5 flex items-center gap-2">
            <Button data-testid="create-foodcard-btn" onClick={() => user ? setShowFoodCardModal(true) : toast({ title: 'Sign in to create FoodCard', variant: 'destructive' })}
              className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white rounded-2xl shadow-lg gap-1.5 h-10 px-5 font-semibold">
              <FoodCardIcon size={22} className="mr-1 text-[#FF6B35]" />Post FoodCard
            </Button>
            <button onClick={handleRefresh} disabled={isLoading} className="h-10 w-10 bg-black/30 backdrop-blur-sm hover:bg-black/50 text-white rounded-2xl flex items-center justify-center transition-colors">
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Tab Bar — matching Promote page pill style */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide gap-1 py-2.5">
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setActiveSection(key)}
                data-testid={`section-tab-${key}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 ${
                  activeSection === key
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'bg-transparent text-gray-500 hover:bg-gray-100'
                }`}>
                <Icon className="w-4 h-4" />
                {label}
                {key === 'people' && people.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeSection === 'people' ? 'bg-white/20 text-white' : 'bg-[#FFF5F0] text-[#FF6B35]'}`}>
                    {people.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5">

        {/* Error */}
        {error && !loading && (
          <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />{error}
            <button onClick={handleRefresh} className="underline ml-auto">Retry</button>
          </div>
        )}

        {/* Active tag filter */}
        {activeTag && (
          <div data-testid="active-tag-filter" className="mb-4 flex items-center gap-2 bg-[#FFF5F0] border border-[#FF6B35]/20 rounded-2xl px-4 py-2.5">
            <Tag className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
            <span className="text-sm text-gray-700">Showing posts tagged</span>
            <span className="text-sm font-bold text-[#FF6B35]">#{activeTag}</span>
            <button onClick={clearTag} data-testid="clear-tag-filter" className="ml-auto p-1 rounded-full hover:bg-[#FF6B35]/10 transition-colors">
              <X className="w-4 h-4 text-gray-400 hover:text-[#FF6B35]" />
            </button>
          </div>
        )}

        {/* Stories row (Feed tab only) */}
        {activeSection === 'feed' && (storyGroups.length > 0 || user) && (
          <div className="mb-5 bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-100 dark:border-gray-800 p-3">
            <StoriesRow
              storyGroups={storyGroups}
              onViewStory={(group) => setViewingStory(group)}
              onCreateStory={() => setShowCreateStory(true)}
            />
          </div>
        )}

        {/* Suggested Users carousel (Feed tab only) */}
        {activeSection === 'feed' && <SuggestedUsers />}

        {/* Trending hashtags bar (Feed tab only) */}
        {activeSection === 'feed' && trendingTags.length > 0 && !activeTag && (
          <div className="mb-5" data-testid="trending-tags-bar">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="w-3.5 h-3.5 text-[#FF6B35]" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Trending</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
              {trendingTags.map(({ tag, count }) => (
                <button
                  key={tag}
                  data-testid={`trending-tag-${tag}`}
                  onClick={() => setSearchParams({ tag })}
                  className="flex items-center gap-1.5 bg-white border border-gray-200 hover:border-[#FF6B35] rounded-full px-3.5 py-1.5 text-sm text-gray-700 hover:text-[#FF6B35] transition-all shadow-sm whitespace-nowrap"
                >
                  <span className="font-medium">#{tag}</span>
                  <span className="text-[10px] bg-[#FFF5F0] text-[#FF6B35] px-1.5 py-0.5 rounded-full font-bold">{count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cuisine pills (for non-feed tabs) */}
        {activeSection !== 'feed' && activeSection !== 'people' && topCuisines.length > 0 && (
          <div className="mb-5">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
              {topCuisines.map(({ name, count }) => (
                <button key={name}
                  onClick={() => setSearchParams({ tag: name })}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#FF6B35] hover:text-[#FF6B35] rounded-2xl px-4 py-2 text-sm font-medium text-gray-700 transition-all shadow-sm whitespace-nowrap">
                  <span>{name}</span>
                  {count > 0 && <span className="bg-[#FFF5F0] text-[#FF6B35] text-xs px-1.5 py-0.5 rounded-full font-bold">{count}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No location */}
        {!locationString && !loading && posts.length === 0 && activeSection === 'feed' && (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">Set your location to see local food posts</p>
          </div>
        )}

        {/* ═══ MAIN CONTENT WITH SIDEBAR LAYOUT ═══ */}
        <div className="flex gap-6">
          {/* Main Feed Column */}
          <div className="flex-1 min-w-0">
            {/* ═══ FEED TAB ═══ */}
            {activeSection === 'feed' && (
              <>
                <div className="space-y-5 max-w-2xl mx-auto lg:mx-0">
                  {loading
                    ? Array.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)
                    : posts.map((post) => (
                      <div key={post.post_id}>
                        <FoodCard variant="post"
                          post={post}
                          onLike={handleLike}
                          onFoodlist={(p) => toggleFoodlist({ name: p.venue_name || 'Dish', restaurant: p.venue_name || '', location: p.city || '', image: p.image_url || '', cuisine: p.cuisine || '' })}
                          onShare={() => handleShare(post.post_id)}
                          onFollow={handleFollow}
                          onEdit={(p) => setEditPost(p)}
                          onDelete={handleDeletePost}
                          onComment={() => {}}
                          followingIds={followingIds}
                          currentUserId={user?.user_id}
                        />
                        <CommentsThread
                          postId={post.post_id}
                          initialCount={post.comments_count || 0}
                          onCountChange={(count) => setPosts(prev => prev.map(p => p.post_id === post.post_id ? { ...p, comments_count: count } : p))}
                        />
                      </div>
                    ))}
                </div>

                {/* Infinite scroll loader */}
                <div ref={loaderRef} className="py-4 flex justify-center">
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <div className="w-5 h-5 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
                      Loading more...
                    </div>
                  )}
                  {!loading && !loadingMore && !hasMore && posts.length > 0 && (
                    <p className="text-xs text-gray-400">You've seen all local posts</p>
                  )}
                </div>
              </>
            )}

        {/* ═══ FOLLOWING TAB ═══ */}
            {activeSection === 'following' && (
              <>
                <div className="space-y-5 max-w-2xl mx-auto lg:mx-0">
                  {followingLoading ? (
                    Array.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)
                  ) : followingError && followingPosts.length === 0 ? (
                    <div className="text-center py-16">
                      <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-700 mb-2">
                        {user ? 'Your Following Feed' : 'Sign In to See Your Feed'}
                      </h3>
                      <p className="text-gray-500 mb-5 text-sm">{followingError}</p>
                      {user ? (
                        <Button onClick={() => setActiveSection('people')}
                          className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl gap-2">
                          <Users className="w-4 h-4" />Find Foodies to Follow
                        </Button>
                      ) : (
                        <Button onClick={() => toast({ title: 'Sign in to continue', variant: 'destructive' })}
                          className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl gap-2">
                          Sign In
                        </Button>
                      )}
                    </div>
                  ) : followingPosts.length === 0 ? (
                    <div className="text-center py-16">
                      <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-700 mb-2">No Posts Yet</h3>
                      <p className="text-gray-500 mb-5 text-sm">People you follow haven't posted anything recently</p>
                      <Button onClick={() => setActiveSection('people')}
                        className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl gap-2">
                        <Users className="w-4 h-4" />Find More Foodies
                      </Button>
                    </div>
                  ) : (
                    followingPosts.map((post) => (
                      <div key={post.post_id} data-testid={`following-post-${post.post_id}`}>
                        <FoodCard variant="post"
                          post={post}
                          onLike={handleLike}
                          onFoodlist={(p) => toggleFoodlist({ name: p.venue_name || 'Dish', restaurant: p.venue_name || '', location: p.city || '', image: p.image_url || '', cuisine: p.cuisine || '' })}
                          onShare={() => handleShare(post.post_id)}
                          onFollow={handleFollow}
                          onEdit={(p) => setEditPost(p)}
                          onDelete={handleDeletePost}
                          onComment={() => {}}
                          followingIds={followingIds}
                          currentUserId={user?.user_id}
                        />
                        <CommentsThread
                          postId={post.post_id}
                          initialCount={post.comments_count || 0}
                          onCountChange={(count) => setFollowingPosts(prev => prev.map(p => p.post_id === post.post_id ? { ...p, comments_count: count } : p))}
                        />
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {/* ═══ FOR YOU / TRENDING TABS ═══ */}
            {(activeSection === 'forYou' || activeSection === 'trending') && (
              <>
                {/* Trending Reposts Section - Show only on Trending tab */}
                {activeSection === 'trending' && (
                  <div className="mb-6">
                    <TrendingReposts />
                  </div>
                )}

                {discoverLoading ? <GridSkeleton /> : displayPosts.length === 0 ? (
                  <div className="text-center py-16">
                    <Sparkles className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-700 mb-2">No posts yet in {city || 'your city'}</h3>
                    <p className="text-gray-500 mb-5 text-sm">Be the first to share a food moment!</p>
                    <Button onClick={() => { setActiveSection('feed'); user ? setShowCreate(true) : toast({ title: 'Sign in to post', variant: 'destructive' }); }}
                      className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl gap-2">
                      <Sparkles className="w-4 h-4" />Create First Post
                    </Button>
                  </div>
                ) : (
                  <>
                    {activeSection === 'trending' && (
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#FF6B35]" />
                        Top Posts
                      </h3>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {displayPosts.map(post => (
                        <FoodCard variant="discover"
                          key={post.post_id}
                          post={post}
                          onLike={handleLike}
                          onSaveToFoodlist={handleSaveToFoodlist}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {/* ═══ PEOPLE TAB ═══ */}
            {activeSection === 'people' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#FF6B35]" />Food Buddies Near You
                </h3>
                <p className="text-xs text-gray-500 mb-4">Based on your Foodlist — people who share your taste</p>
                {discoverLoading ? (
                  <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" /></div>
                ) : people.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500 mb-3">Save dishes to your Foodlist to find food buddies!</p>
                    <Link to="/recommendations">
                      <Button size="sm" className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl">Explore Recommendations</Button>
                    </Link>
                  </div>
                ) : (
                  people.map(s => (
                    <PeopleCard key={s.user_id} suggestion={s} onFollow={(id, isFol) => handleFollow(id, isFol)} isFollowing={followingIds.has(s.user_id)} />
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FF6B35]" />Your Taste Profile
                </h3>
                <p className="text-xs text-gray-500 mb-4">See your food personality and top preferences</p>
                <Link to="/profile">
                  <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-[#FFF9F0] to-[#FFF5F0] rounded-2xl border border-[#FF6B35]/15 hover:border-[#FF6B35]/40 transition-all cursor-pointer">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-2xl flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">View My Taste Profile</p>
                      <p className="text-xs text-gray-500">Badges, top cuisines, recent searches</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}
          </div>
          
          {/* ═══ SIDEBAR COLUMN (Desktop Only) ═══ */}
          <div className="hidden lg:block w-80 flex-shrink-0 space-y-4 sticky top-4 self-start">
            <WhatsCookingSidebar />
            <TrendingReposts className="mt-4" />
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreate && (
        <CreatePostModal
          onClose={() => setShowCreate(false)}
          onPostCreated={handlePostCreated}
        />
      )}

      {/* Create FoodCard Modal - Clean, AI-powered */}
      <CreateFoodCardModal
        isOpen={showFoodCardModal}
        onClose={() => setShowFoodCardModal(false)}
        onCardCreated={() => { fetchPosts(); setShowFoodCardModal(false); }}
      />

      {editPost && (
        <EditPostModal
          post={editPost}
          onClose={() => setEditPost(null)}
          onUpdated={handlePostEdited}
        />
      )}

      {/* Create Story Modal */}
      {showCreateStory && (
        <CreateStoryModal
          onClose={() => setShowCreateStory(false)}
          onCreated={() => fetchStories()}
        />
      )}

      {/* Story Viewer */}
      {viewingStory && (
        <StoryViewer
          group={viewingStory}
          onClose={() => { setViewingStory(null); fetchStories(); }}
        />
      )}

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(o) => setReviewModal({ open: o, post: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rate this post</DialogTitle>
            <DialogDescription>Share your experience</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-2 py-4">
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setReviewRating(s)}
                className="text-3xl transition-transform hover:scale-110 active:scale-90">
                {s <= reviewRating ? '\u2B50' : '\u2606'}
              </button>
            ))}
          </div>
          <Textarea placeholder="What did you think?" value={reviewText} onChange={e => setReviewText(e.target.value)} className="resize-none" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, post: null })}>Cancel</Button>
            <Button onClick={() => {
              toast({ title: 'Review submitted!' });
              setReviewModal({ open: false, post: null });
              setReviewRating(0); setReviewText('');
            }} className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white rounded-2xl">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Foodie;
